import { useState, useMemo, useRef, useEffect, createElement, Fragment, cloneElement } from 'react';
import cn from 'classnames';
import { v4 } from 'uuid';
import { useLocalization } from '../Provider/Provider.js';
import { useProxiedRef } from '../../utils/hooks/useProxiedRef.js';
import { createCustomEvent } from '../../utils/input.js';
import { useFlattenedData, findByValue, setInputValueByRef, sanitizeItem } from '../Listbox/util.js';

const useSelect = ({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  data = [],
  defaultValue,
  disabled,
  emptyValue,
  id: nativeId,
  multiselect,
  onBlur,
  onClick,
  onChange,
  readOnly,
  value,
  ...otherProps
}, ref) => {
  const {
    texts
  } = useLocalization();
  const flattenedData = useFlattenedData(data);
  const listboxInputRef = useProxiedRef(ref);
  const [open, setOpen] = useState(false);
  const id = useMemo(() => nativeId || `select_${v4()}`, [nativeId]);
  const inputRef = useRef(null);
  const buttonId = `${id}-button`; // support 'escape' resetting to the value that was set when the listbox opened

  const [lastValue, setLastValue] = useState(value);
  useEffect(() => {
    setLastValue(value);
  }, [open]);
  useEffect(() => {
    if (value === undefined) {
      if (defaultValue !== undefined && findByValue(flattenedData, defaultValue)) {
        setInputValueByRef(inputRef.current, defaultValue);
      } else {
        if (emptyValue !== undefined) {
          setInputValueByRef(inputRef.current, emptyValue);
        } else if (data.length > 0) {
          setInputValueByRef(inputRef.current, data[0].value);
        }
      }
    } else {
      if (!multiselect && !findByValue(flattenedData, value)) {
        if (emptyValue !== undefined) {
          setInputValueByRef(inputRef.current, emptyValue);
        } else if (data.length > 0) {
          setInputValueByRef(inputRef.current, data[0].value);
        }
      }
    }
  }, []); // event handlers

  const handleButtonKeyDown = event => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
    }
  };

  const handleListboxKeyDown = event => {
    switch (event.key) {
      case 'Escape':
        {
          event.preventDefault();

          if (lastValue !== undefined) {
            setInputValueByRef(listboxInputRef.current, lastValue);
          }

          setOpen(false);
          break;
        }

      case 'Tab':
      case 'Enter':
        {
          if (event.key !== 'Tab') {
            event.preventDefault();
          }

          setOpen(false);
          break;
        }
    }

    if (otherProps.onKeyDown) {
      otherProps.onKeyDown(event);
    }
  };

  const handleListboxClick = event => {
    event.preventDefault();

    if (!multiselect) {
      setOpen(false);

      if (onBlur) {
        onBlur(createCustomEvent('blur'));
      }
    }
  };

  let text = '';
  let more = 0;

  if (value !== undefined) {
    if (multiselect) {
      const selectedValues = (value === null || value === void 0 ? void 0 : value.toString().split(',')) || [];

      if (selectedValues.length === flattenedData.filter(item => !item.disabled).length) {
        text = texts.select.allOptionsSelected;
      } else {
        var _findByValue$text, _findByValue;

        text = (_findByValue$text = (_findByValue = findByValue(flattenedData, selectedValues[0])) === null || _findByValue === void 0 ? void 0 : _findByValue.text) !== null && _findByValue$text !== void 0 ? _findByValue$text : '';
        more = selectedValues.length > 1 ? selectedValues.length - 1 : 0;
      }
    } else {
      const item = findByValue(flattenedData, value);

      if (item) {
        text = item.icon ? createElement(Fragment, null, cloneElement(item.icon, {
          className: cn(item.icon.props.className, 'mr-1 -mt-px')
        }), item.text) : item.text;
      }
    }
  }

  const handleInputChange = event => {
    event.persist();

    if (onChange) {
      var _item$path$split, _item$path;

      const item = findByValue(flattenedData, event.target.value);
      event.detail = sanitizeItem(item);
      const indexes = (_item$path$split = item === null || item === void 0 ? void 0 : (_item$path = item.path) === null || _item$path === void 0 ? void 0 : _item$path.split('.')) !== null && _item$path$split !== void 0 ? _item$path$split : [];

      if (indexes.length > 1) {
        // we don't want to map the current item
        indexes.pop(); // we need to rebuild the path as we map

        let lastPath;
        event.detail.parents = indexes.map(i => {
          lastPath = lastPath ? [lastPath, i].join('.') : i;
          return sanitizeItem(flattenedData.find(i => i.path === lastPath));
        });
      }

      onChange(event);
    }
  };

  const button = {
    'aria-haspopup': 'listbox',
    'aria-label': ariaLabel ? `${ariaLabel} ${text}` : undefined,
    'aria-labelledby': ariaLabelledBy ? `${ariaLabelledBy} ${buttonId}` : undefined,
    disabled: disabled || readOnly,
    id: buttonId,
    onKeyDown: !disabled && !readOnly ? handleButtonKeyDown : undefined,
    type: 'button'
  };
  const listbox = { ...otherProps,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    data,
    disabled,
    emptyValue,
    onBlur,
    onClick: handleListboxClick,
    onChange: event => {
      setInputValueByRef(inputRef.current, event.target.value);
    },
    onKeyDown: handleListboxKeyDown,
    ref: listboxInputRef,
    value
  };
  const input = {
    onChange: handleInputChange,
    ref: inputRef,
    value: String(value !== null && value !== void 0 ? value : '')
  };
  return {
    button,
    listbox,
    input,
    popover: {
      open,
      onOpenChange: open => {
        if (!open && onBlur) {
          onBlur(createCustomEvent('blur'));
        }

        setOpen(open);
      }
    },
    text,
    more
  };
};

export { useSelect };
//# sourceMappingURL=useSelect.js.map
